import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';

import {
  controlFieldText,
  controlFocus,
  controlInvalid,
  controlSurface,
  controlSurfaceHover,
  controlTransition,
} from '../styles/control';

/**
 * Read-only — value and border at 80%: inert, but still focusable and copyable.
 *
 * Textarea-only until a second control needs it (Input parked read-only at
 * DS-1499). Value contrast holds at 9.67:1 / 11.69:1.
 *
 * - `enabled:` — `:read-only` also matches a *disabled* field, which would then
 *   take both tints.
 * - `:not([aria-invalid])` — these outrank `controlInvalid`, so without it a
 *   locked field loses its error border.
 * - hover/active restated so the border doesn't step a ramp you can't act on;
 *   focus restated back over that.
 */
export const textareaReadOnlyClassesExperimental = [
  "[&:not([aria-invalid='true'])]:enabled:read-only:border-control-border/80",
  'enabled:read-only:text-control-text-value/80',
  "[&:not([aria-invalid='true'])]:enabled:read-only:hover:border-control-border/80",
  "[&:not([aria-invalid='true'])]:enabled:read-only:active:border-control-border/80",
  "[&:not([aria-invalid='true'])]:enabled:read-only:focus-visible:border-control-border-focus",
  "[&:not([aria-invalid='true'])]:enabled:read-only:hover:focus-visible:border-control-border-focus",
].join(' ');

/**
 * Firefox-only mask over its native resize grip, on the field's own fill.
 *
 * Webkit hides its grip with `[&::-webkit-resizer]:hidden` and never needed the
 * mask — there the 16px square just covered the end of the scrollbar thumb.
 * `@supports (-moz-appearance)` is the engine test.
 */
export const textareaResizeMaskClassesExperimental = [
  'bg-control-background',
  'hidden supports-[-moz-appearance:none]:block',
].join(' ');

/**
 * The resize handle as a control glyph: 50% → full on hover/focus. Keyed to the
 * wrapper's `group` because the handle is `pointer-events-none`.
 *
 * `transition-[color,opacity]` — one tailwind-merge group, so `transition-colors`
 * would drop the disabled fade.
 */
export const textareaResizeHandleClassesExperimental = [
  'text-control-icon/50 transition-[color,opacity] duration-150',
  'group-hover:text-control-icon group-focus-within:text-control-icon',
].join(' ');

/**
 * Experimental Textarea styles, behind `release-experimental-design-system-forms`.
 * One element paints and receives the pointer, so it takes the plain control
 * constants rather than Input's `*Within` twins.
 *
 * `min-h-[80px]`, not `controlHeight`: multi-line, so it is the one member of
 * the tier exempt from the single-row height.
 *
 * Filled has no treatment — value vs. placeholder colour is already the whole
 * difference between a filled field and an empty one.
 */
export const textareaVariantsExperimental = cva(
  cn(
    controlFieldText,
    controlSurface,
    'flex min-h-[80px] w-full rounded-lg px-3 py-2 disabled:cursor-not-allowed',
    'disabled:border-control-border-disabled disabled:bg-control-background-disabled',
    // House scrollbar, for content past `rows` / `maxRows`: a 6px flush thumb
    // instead of the platform's ~15px gutter eating into `px-3`.
    'scrollbar-subtle',
    // A scrollbar isn't clipped by `border-radius` in any engine, so the thumb's
    // ends poke through the corners. 8px is the exact tangent for a 6px thumb
    // inside `rounded-lg` less the border — and equals `py-2`, so the thumb
    // lines up with the first row of text. Re-derive if either changes.
    '[&::-webkit-scrollbar-track]:my-2',
    // A custom webkit scrollbar sits inside the element's box and inherits its
    // `text` cursor. Arrow instead, on both parts — `cursor` inherits, but not
    // reliably enough here to lean on.
    '[&::-webkit-scrollbar-thumb]:cursor-default [&::-webkit-scrollbar]:cursor-default',
    controlTransition,
    controlSurfaceHover,
    controlFocus,
    // Order matters: invalid must resolve over focus, and read-only over both.
    controlInvalid,
    textareaReadOnlyClassesExperimental
  )
);
